/**
 * ============================================================================
 * MIDWAY CLIENT AUTH — session establishment + alias for a static SPA.
 * ============================================================================
 *
 * The deployed website is plain CloudFront/S3: the page is served anonymously,
 * so nothing on the server side ever logs the user in or tells us who they
 * are. This module implements the standard client-side Midway SSO flow that
 * internal static SPAs use (pattern verified against
 * KilowattCommonReactComponents, WhipOpsHub, GreenlandAdminWebapp, COEUI):
 *
 *  1. SILENT CHECK — fetch `midway-auth.amazon.com/SSO` with credentials.
 *     A live Midway session returns an id_token JWT in the body; its `sub`
 *     claim is the caller's alias. On a cold load Midway sometimes 403s the
 *     first cross-site mint while rotating its session cookie, so a single
 *     immediate retry is built in.
 *  2. REDIRECT — no session means a full-page bounce to `/SSO/redirect`;
 *     Midway authenticates (security-key prompt) and 302s back to the exact
 *     URL the user asked for with `id_token` in the query string, which we
 *     consume and strip from the address bar.
 *  3. RECOVERY — when a middleware call later fails with an auth error, the
 *     transport asks us to re-run the silent check: if the Midway session is
 *     dead we redirect; if it is alive the failure was an authorization
 *     denial (e.g. Bindle) and must surface, not loop through login.
 *
 * The id_token is DISPLAY IDENTITY ONLY. It is never sent to our API and no
 * trust boundary moves to the client — the middleware keeps authenticating
 * every call through its own Midway authorizer (SentryFetch/MCS transport,
 * see ReportingApiClient).
 *
 * Native `fetch`, not SentryFetch: this IS the Midway host, and the coding
 * standard routes known non-middleware calls to the native transport.
 *
 * Spec: .kiro/specs/midway-client-auth
 */

import { setMidwayAlias } from 'src/accessors/currentUser';
import { IS_EMBEDDED } from 'src/config/embedMode';

const MIDWAY_HOST = 'https://midway-auth.amazon.com';
const ID_TOKEN_PARAM = 'id_token';

/**
 * Loop guard that survives the login round trip. The module-level
 * `redirecting` flag is wiped by the full-page navigation, so on its own a
 * broken return leg (Midway bounces back without a usable id_token — e.g.
 * third-party-cookie blocking that also breaks the silent mint) would
 * redirect again on reload, forever. This sessionStorage marker is set just
 * before navigating and cleared the moment a token is successfully obtained;
 * if it is still set when we want to redirect again, we fall open instead.
 */
const REDIRECT_MARKER = 'wbr.midway.redirectAttempted';

/**
 * The nonce of the login redirect currently in flight, persisted so it
 * survives the round trip. The return-leg token is accepted ONLY when its
 * `nonce` claim matches this value (single-use): a crafted link carrying a
 * forged `?id_token=` has no pending nonce and is rejected outright
 * (CWE-352 — same pattern as VCareUnifiedDashboardUI /
 * HelianthusArbiterServiceFrontend / KimaOperationsHubWebApp).
 */
const PENDING_NONCE_KEY = 'wbr.midway.nonce';

export type MidwayAuthResult =
  /** Live session (silent or return leg); alias registered when decodable. */
  | 'authenticated'
  /** No session; the page is navigating to Midway login right now. */
  | 'redirecting'
  /** Host cannot complete the flow (standalone dev server); nothing done. */
  | 'skipped'
  /**
   * No session AND a login redirect was already attempted this browsing
   * session without producing a token — fall open rather than loop. The app
   * renders; the middleware's own auth remains the enforcement point.
   */
  | 'unavailable'
  /**
   * No session AND we are framed (embedded mode). The frame must not navigate
   * to Midway login — midway-auth.amazon.com refuses to be framed — so the
   * gate shows a top-level sign-in prompt instead of the app.
   */
  | 'sign-in-required';

/** At most one login navigation per page load (R1.2, R3.3). */
let redirecting = false;

/** Memoized bootstrap flow, so a StrictMode double-mount runs it once. */
let sessionPromise: Promise<MidwayAuthResult> | undefined;

/** One recovery check at a time — parallel failing calls share it (R3.3). */
let recoveryInFlight: Promise<void> | undefined;

/**
 * The standalone dev server (plain-HTTP localhost) cannot complete the Midway
 * flow — no redirect, no cross-site cookie — and keeps its existing behavior.
 * Everything HTTPS (deployed stages, the mkcert local.* Midway dev setup) can.
 */
function isMidwayCapableHost(): boolean {
  const { protocol, hostname } = window.location;
  return (
    protocol === 'https:' && hostname !== 'localhost' && hostname !== '0.0.0.0'
  );
}

/** OIDC replay-guard nonce; crypto when available, Math.random in old jsdom. */
function generateNonce(): string {
  const cryptoObj = window.crypto;
  if (cryptoObj?.getRandomValues) {
    return Array.from(cryptoObj.getRandomValues(new Uint8Array(16)), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('');
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** The current URL minus any stale token params — where Midway returns to. */
function currentUrlWithoutToken(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(ID_TOKEN_PARAM);
  url.searchParams.delete('state');
  return url.toString();
}

/**
 * `/SSO` (fetch-based silent mint) or `/SSO/redirect` (full-page login
 * bounce). Same parameter set as the reference implementations. The nonce is
 * supplied by the caller so it can validate the returned token against it.
 */
function buildSsoUrl(redirect: boolean, nonce: string): string {
  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: window.location.origin,
    redirect_uri: currentUrlWithoutToken(),
    scope: 'openid',
    nonce,
  });
  return `${MIDWAY_HOST}/SSO${redirect ? '/redirect' : ''}?${params}`;
}

/** Decoded (NOT signature-verified — see validatedAlias) token payload. */
function decodeClaims(jwt: string): Record<string, unknown> | undefined {
  try {
    const payload = jwt.split('.')[1];
    const json = window.atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Validate the token's claims and extract its `sub` alias; undefined = reject.
 *
 * Checks (Shepherd CodeGuard, missing-jwt-signature-or-claim-validation):
 * - `nonce` must equal the nonce THIS page generated for THIS mint/redirect —
 *   a token planted in a crafted link cannot know it (single-use, consumed).
 * - `exp` must not have passed.
 * - `aud`, when present, must include our origin (the client_id we sent).
 *
 * The SIGNATURE is deliberately not verified client-side: this alias is
 * display identity only (never sent to our API; the middleware authenticates
 * every call with its own Midway authorizer), and a client-side JWKS check
 * would add a network dependency to defend a value the server re-checks
 * anyway. The nonce binding above already defeats the attacker-controllable
 * return-leg vector, which is the finding's exploitable path. Accepted risk,
 * matching the reference frontends (VCareUnifiedDashboardUI, Helianthus
 * ArbiterServiceFrontend, KimaOperationsHubWebApp, AlliancePortalWebsite).
 */
function validatedAlias(
  jwt: string,
  expectedNonce: string,
): string | undefined {
  const claims = decodeClaims(jwt);
  if (!claims) return undefined;
  if (claims.nonce !== expectedNonce) return undefined;
  if (typeof claims.exp === 'number' && claims.exp * 1000 < Date.now()) {
    return undefined;
  }
  if (claims.aud !== undefined) {
    const auds = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    if (!auds.includes(window.location.origin)) return undefined;
  }
  const sub = claims.sub;
  return typeof sub === 'string' && sub.trim() ? sub.trim() : undefined;
}

/**
 * A VALIDATED alias proves the login flow works here: register it and re-arm
 * the loop guard so a FUTURE genuine expiry may redirect again.
 */
function adoptValidatedAlias(alias: string): void {
  setMidwayAlias(alias);
  try {
    window.sessionStorage.removeItem(REDIRECT_MARKER);
  } catch {
    // sessionStorage unavailable: the guard simply stays conservative.
  }
}

/** Take (and delete — single use) the nonce of the redirect in flight. */
function consumePendingNonce(): string | undefined {
  try {
    const nonce = window.sessionStorage.getItem(PENDING_NONCE_KEY);
    window.sessionStorage.removeItem(PENDING_NONCE_KEY);
    return nonce ?? undefined;
  } catch {
    return undefined;
  }
}

/** True when a previous redirect this browsing session produced no token. */
function redirectAlreadyAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(REDIRECT_MARKER) !== null;
  } catch {
    return false; // Can't know — allow the (single, flag-guarded) redirect.
  }
}

/**
 * Outcome of a silent mint. 'no-session' and 'unreachable' are deliberately
 * distinct: Midway ANSWERING that there is no session justifies a login
 * redirect, while a network failure reaching Midway proves nothing about the
 * session — recovery must not bounce the user through login over a blip.
 */
type SilentMint =
  | { outcome: 'session'; alias: string }
  | { outcome: 'no-session' }
  | { outcome: 'unreachable' };

/**
 * Silent id_token mint against the live Midway session. Each attempt sends a
 * fresh nonce and accepts only a token that echoes it and passes claim
 * validation. One retry when the first attempt yields no session: the
 * cold-load mint can 403 while Midway rotates its session cookie, and the
 * immediate retry with the fresh cookie succeeds. When neither attempt
 * yields a session, the LAST attempt's outcome classifies the failure.
 */
async function silentToken(): Promise<SilentMint> {
  const attempt = async (): Promise<SilentMint> => {
    const nonce = generateNonce();
    try {
      const response = await fetch(buildSsoUrl(false, nonce), {
        credentials: 'include',
      });
      if (!response.ok) return { outcome: 'no-session' };
      // The body read is inside the guard too: a mid-stream network failure
      // is 'unreachable' like any other, never an escaping exception.
      const text = (await response.text()).trim();
      // A validated JWT is the only body a healthy mint returns; anything
      // else (an HTML interstitial, an empty body, a token that fails claim
      // validation) means no usable session.
      const alias = validatedAlias(text, nonce);
      return alias ? { outcome: 'session', alias } : { outcome: 'no-session' };
    } catch {
      return { outcome: 'unreachable' };
    }
  };
  const first = await attempt();
  if (first.outcome === 'session') return first;
  return attempt();
}

/**
 * Return leg of the login redirect: token in the query string. Adopted only
 * when it carries the nonce of the redirect THIS browsing session initiated —
 * an unsolicited or forged `?id_token=` (crafted link) has no pending nonce,
 * or the wrong one, and is stripped and ignored; the caller then proceeds
 * with the normal silent mint, so a legitimate user still authenticates and
 * an attacker-supplied token influences nothing.
 */
function consumeReturnedToken(): boolean {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(ID_TOKEN_PARAM);
  if (!token) return false;
  try {
    // Strip the token from the address bar first, adopted or not (a
    // bookmarked/shared URL must not carry it).
    window.history.replaceState({}, document.title, currentUrlWithoutToken());
  } catch {
    // history unavailable: cosmetic only, the app still works.
  }
  const expectedNonce = consumePendingNonce();
  if (!expectedNonce) return false; // Unsolicited callback — reject.
  const alias = validatedAlias(token, expectedNonce);
  if (!alias) return false; // Failed validation — reject, mint silently.
  adoptValidatedAlias(alias);
  return true;
}

/**
 * Full-page bounce to Midway login; fires at most once per page load AND at
 * most once per browsing session while no token has come back (the
 * REDIRECT_MARKER breaks the redirect → broken return leg → redirect loop).
 * Returns false when the redirect was withheld by the loop guard.
 */
function redirectToMidwayLogin(): boolean {
  if (redirecting) return true;
  if (redirectAlreadyAttempted()) return false;
  const nonce = generateNonce();
  try {
    window.sessionStorage.setItem(REDIRECT_MARKER, '1');
    // Persist the nonce across the round trip so the return-leg token can be
    // validated against it (consumeReturnedToken). If storage is unavailable
    // the return leg will find no pending nonce, reject the URL token, and
    // authenticate through the silent mint instead — still correct.
    window.sessionStorage.setItem(PENDING_NONCE_KEY, nonce);
  } catch {
    // sessionStorage unavailable: proceed — the in-memory flag still
    // guarantees a single navigation for this page load.
  }
  redirecting = true;
  window.location.assign(buildSsoUrl(true, nonce));
  return true;
}

async function establishSession(): Promise<MidwayAuthResult> {
  if (!isMidwayCapableHost()) return 'skipped';
  if (consumeReturnedToken()) return 'authenticated';
  const mint = await silentToken();
  if (mint.outcome === 'session') {
    adoptValidatedAlias(mint.alias);
    return 'authenticated';
  }
  // Framed: a full-page bounce would navigate the FRAME to Midway, which
  // refuses to be framed. The silent mint above works in-frame (Midway's own
  // cookies are SameSite=None), so this is only reached with no session.
  if (IS_EMBEDDED) return 'sign-in-required';
  // At bootstrap both failure classes redirect: with no session the login
  // bounce is the point, and with Midway unreachable the app cannot work
  // anyway (every internal site surfaces Midway's own error page here).
  return redirectToMidwayLogin() ? 'redirecting' : 'unavailable';
}

/**
 * Bootstrap entry (MidwayAuthGate). Memoized: every caller shares one flow.
 */
export function ensureMidwaySession(): Promise<MidwayAuthResult> {
  if (!sessionPromise) sessionPromise = establishSession();
  return sessionPromise;
}

/**
 * Mid-session expiry recovery (ReportingApiClient, on AuthFailure). Re-runs
 * the silent check: dead session → login redirect; live session → the 401/403
 * was an authorization denial and the caller's error must surface unchanged.
 * Midway unreachable → do nothing: a network blip proves nothing about the
 * session, and bouncing a possibly-authenticated user through a full-page
 * login over it would be worse than letting the error surface (R3.2).
 */
export function recoverMidwaySession(): Promise<void> {
  if (!isMidwayCapableHost() || redirecting) return Promise.resolve();
  if (!recoveryInFlight) {
    recoveryInFlight = (async () => {
      try {
        const mint = await silentToken();
        if (mint.outcome === 'session') adoptValidatedAlias(mint.alias);
        // Framed: never navigate the frame to Midway (see establishSession);
        // the next API failure surfaces as an error the user can act on.
        else if (mint.outcome === 'no-session' && !IS_EMBEDDED) redirectToMidwayLogin();
      } catch {
        // Recovery is best-effort and fire-and-forget (`void recover…()` in
        // the transport) — it must never surface an unhandled rejection.
      } finally {
        recoveryInFlight = undefined;
      }
    })();
  }
  return recoveryInFlight;
}

/** TEST ONLY — reset the module's one-shot state between cases. */
export function resetMidwayAuthForTests(): void {
  redirecting = false;
  sessionPromise = undefined;
  recoveryInFlight = undefined;
  try {
    window.sessionStorage.removeItem(REDIRECT_MARKER);
    window.sessionStorage.removeItem(PENDING_NONCE_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}
